import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MessagePane } from "@/features/mail/MessagePane";
import {
  listMailboxes,
  getMailAccountStatus,
  getMailboxMessage,
  restoreMailboxMessage,
  getOwnMailFolders,
  isPersonalMailbox,
  mailKeys,
  personalMailboxFrom,
  type SharedMailbox,
} from "@/features/mail/mail.api";
import type { MailUndo } from "@/features/mail/mailUndo";
import { customFolderName, fixedFolderLabelKey } from "@/features/mail/mailFolders";
import { fetchSession, sessionQueryKey, hasModule } from "@/lib/auth";
import { Skeleton } from "@/components/feedback/Skeleton";

// One mail in a window of its own, opened from the reading pane. Deliberately
// OUTSIDE the _shell layout: a window that carries the sidebar, the topbar and
// the breadcrumbs is a second copy of the application, not a mail.
//
// It shares nothing with the window it came from except the session cookie and
// the server. That is the whole trade — see the "Mail in een apart venster"
// section in docs/mail.md — and it is why filing a mail here closes the window
// rather than pretending to steer a list it cannot see.

export const Route = createFileRoute("/mail-window")({
  // The same guard as the shell: a second window shares the httpOnly session
  // cookie, so this normally passes without a round trip. Without it a popped-out
  // window of a signed-out session would render an empty frame instead of asking
  // for a login.
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    // The module check is NOT a redirect. Every route this app would send you
    // to lives under _shell, so redirecting here turns the little mail window
    // into the whole application — and sending a signed-in user to /login shows
    // them SSO buttons for an account they are already using. The component
    // says no instead.
    return { session };
  },
  validateSearch: (search: Record<string, unknown>) => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const files = Number(search["files"]);
    return {
      mailbox: str(search["mailbox"]),
      msg: str(search["msg"]),
      folder: str(search["folder"]),
      // An answer moved here from the main window (MessagePane, popOutReply):
      // it opens at once, with the files that are handed across. The router
      // writes `reply=1` back as `reply=true` and reads that as a boolean.
      reply: search["reply"] === true || search["reply"] === 1 || search["reply"] === "1" ? true : undefined,
      files: Number.isInteger(files) && files > 0 ? files : undefined,
    };
  },
  component: MailWindow,
});

function MailWindow() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const { session } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const mailboxId = search.mailbox ?? "";
  const messageId = search.msg ?? "";
  const personalWanted = isPersonalMailbox(mailboxId);
  // "Ongelezen laten" is a per-browser toggle on the mail screen; without it a
  // popped-out mail is always marked read, silently ignoring the choice made
  // two clicks away.
  // Filed, deleted or reported: the mail has left the folder this window was
  // opened on. The window closes — but not before offering the way back. In the
  // main window that offer lives in the list; here there is no list, so the
  // window stays up for it and closes itself afterwards. Without this, filing
  // from a pop-out was the one place in the app where those buttons had no undo.
  const [done, setDone] = useState<MailUndo | null>(null);
  // The answer this window was opened for has gone: the window closes. It
  // closes from an effect, once the pane with its unsent-mail warning is gone.
  const [replied, setReplied] = useState(false);
  useEffect(() => {
    if (replied) window.close();
  }, [replied]);
  const [keepUnread] = useState(() => {
    try {
      return localStorage.getItem("wlx:mailKeepUnread") === "1";
    } catch {
      return false;
    }
  });

  const canMail = hasModule(session, "mail") || hasModule(session, "mail_personal");

  const mailboxesQuery = useQuery({
    queryKey: mailKeys.mailboxes,
    queryFn: () => listMailboxes(),
    enabled: !personalWanted && Boolean(mailboxId),
  });
  const personalQuery = useQuery({
    queryKey: mailKeys.account,
    queryFn: getMailAccountStatus,
    enabled: personalWanted,
  });

  // The personal inbox as a mailbox — `ready`, because a window can only show
  // a mail and a dead refresh token cannot answer for one.
  //
  // For a shared mailbox the MEMBERSHIP filter matters as much as the id:
  // listMailboxes hands an admin every registered mailbox, and rendering one
  // they never joined would fetch a 403 and read as "this mail is gone".
  // Your own folders, so "Verplaatsen naar" in a window offers what the list does.
  const ownFoldersQuery = useQuery({
    queryKey: mailKeys.ownFolders,
    queryFn: getOwnMailFolders,
    enabled: personalWanted,
    retry: false,
  });
  const personal = personalWanted ? personalMailboxFrom(personalQuery.data, t("mail.myMailbox"), { ready: true }) : null;
  const mailbox: SharedMailbox | null = personalWanted
    ? personal && { ...personal, folders: ownFoldersQuery.data?.folders ?? [] }
    : (mailboxesQuery.data?.find(
        (m) => m.id === mailboxId && m.members.some((mem) => mem.id === session.userId),
      ) ?? null);

  // The SAME key MessagePane uses for its own body fetch (images and text both
  // off → an empty variant), so the window opens one request instead of two
  // identical ones and the pane mounts warm.
  const messageQuery = useQuery({
    queryKey: mailKeys.message(mailboxId, messageId, ""),
    queryFn: () => getMailboxMessage(mailboxId, messageId, { images: false, text: false }),
    enabled: Boolean(mailboxId) && Boolean(messageId),
    retry: false,
    staleTime: 60_000,
  });

  // The window's own name in the taskbar. Without it every popped-out mail is
  // called "Willems Logistics" and they are impossible to tell apart.
  const subject = messageQuery.data?.subject ?? null;
  useEffect(() => {
    if (subject) document.title = subject;
  }, [subject]);

  const loading =
    messageQuery.isLoading ||
    (personalWanted ? personalQuery.isLoading : mailboxesQuery.isLoading);

  if (!canMail) {
    return <WindowNote text={t("mail.windowNoAccess")} />;
  }
  if (!mailboxId || !messageId) {
    return <WindowNote text={t("mail.windowMissing")} />;
  }
  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <Skeleton height="18px" width="40%" />
        <div style={{ marginTop: "16px" }}>
          <Skeleton height="240px" />
        </div>
      </div>
    );
  }
  if (done) {
    return <WindowDone undo={done} mailboxId={mailboxId} />;
  }
  // A window the script did not open does not close; then it says so.
  if (replied) {
    return <WindowNote text={t("mail.replySentWindow")} />;
  }
  if (!mailbox || !messageQuery.data) {
    return <WindowNote text={t("mail.windowGone")} />;
  }

  return (
    <div
      data-screen-label="Back-office Mail Window"
      style={{ height: "100vh", overflow: "hidden", background: "var(--surface-base)" }}
    >
      <MessagePane
        mailbox={mailbox}
        message={messageQuery.data}
        folder={search.folder ?? "inbox"}
        // No list here to advance through or refresh; a linked task belongs to
        // the same cache the rest of the app invalidates.
        onTaskLinked={() => queryClient.invalidateQueries({ queryKey: ["tasks"] })}
        // Filed, deleted or answered: the mail has left the folder this window
        // was opened on, so the window has nothing left to show. Closing is the
        // honest end — undo lives in the window that owns the list.
        onProcessed={(undo) => setDone(undo ?? { id: messageId, action: "processed", restore: "inbox" })}
        expanded
        inOwnWindow
        canAi={hasModule(session, "ai")}
        canCalendar={hasModule(session, "calendar")}
        keepUnread={keepUnread}
        openReplyOnMount={search.reply}
        replyFilesToReceive={search.files ?? 0}
        onReplySent={search.reply ? () => setReplied(true) : undefined}
      />
    </div>
  );
}

// The moment after filing: what happened, the way back, and a window that shuts
// itself so it does not pile up.
function WindowDone({ undo, mailboxId }: { undo: MailUndo; mailboxId: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(10);

  useEffect(() => {
    if (busy) return;
    if (seconds <= 0) {
      window.close();
      return;
    }
    const timer = window.setTimeout(() => setSeconds((v) => v - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds, busy]);

  async function putItBack() {
    setBusy(true);
    try {
      await restoreMailboxMessage(mailboxId, undo.id, undo.restore);
    } finally {
      window.close();
    }
  }

  return (
    <div className="wl-vstack" style={{ gap: "14px", padding: "48px 32px", alignItems: "center", textAlign: "center" }}>
      <p className="t-body">
        {undo.action === "moved" && undo.target
          ? t("mail.movedUndoText", {
              folder: customFolderName(undo.target) ?? t(fixedFolderLabelKey(undo.target) as never),
            })
          : t(
              undo.action === "deleted"
                ? "mail.deletedUndoText"
                : undo.action === "spam"
                  ? "mail.spamUndoText"
                  : "mail.processedUndoText",
            )}
      </p>
      <div className="wl-hstack" style={{ gap: "8px" }}>
        <button className="wl-btn wl-btn--secondary" onClick={() => void putItBack()} disabled={busy}>
          {t("mail.processedUndo")}
        </button>
        <button className="wl-btn wl-btn--ghost" onClick={() => window.close()} disabled={busy}>
          {t("mail.windowClose", { seconds })}
        </button>
      </div>
    </div>
  );
}

function WindowNote({ text }: { text: string }) {
  return (
    <div style={{ padding: "32px", textAlign: "center" }}>
      <p className="t-body-sm t-muted">{text}</p>
    </div>
  );
}
